/**
 * Component: Availability tooltip
 * Reveals the delivery hint next to the availability date on hover, focus and tap.
 * Closing is left to Escape, mouse-out, blur and an outside click: a tap fires the
 * synthesised mouseenter first, so a toggling click would close what it just opened.
 */
(() => {
  const TRIGGER_SELECTOR = ".availability-message__tooltip-trigger";
  const BUBBLE_SELECTOR = ".availability-message__tooltip-bubble";
  const OPEN_CLASS = "_open";

  class AvailabilityTooltip extends HTMLElement {
    connectedCallback() {
      this.trigger = this.querySelector(TRIGGER_SELECTOR);
      this.bubble = this.querySelector(BUBBLE_SELECTOR);
      if (!this.trigger || !this.bubble) return;

      this.listeners = new AbortController();
      const { signal } = this.listeners;

      this.trigger.addEventListener("click", this.open.bind(this), { signal });
      this.addEventListener("mouseenter", this.open.bind(this), { signal });
      this.addEventListener("mouseleave", this.close.bind(this), { signal });
      this.addEventListener("focusin", this.open.bind(this), { signal });
      this.addEventListener("focusout", this.close.bind(this), { signal });
      this.addEventListener("keydown", this.handleKeydown.bind(this), { signal });
      document.addEventListener("click", this.handleDocumentClick.bind(this), { signal });
    }

    disconnectedCallback() {
      this.listeners?.abort();
    }

    get isOpen() {
      return this.classList.contains(OPEN_CLASS);
    }

    open() {
      this.classList.add(OPEN_CLASS);
      this.bubble.hidden = false;
    }

    close() {
      this.classList.remove(OPEN_CLASS);
      this.bubble.hidden = true;
    }

    handleKeydown(event) {
      if (event.key !== "Escape" || !this.isOpen) return;

      this.close();
      this.trigger.focus();
    }

    /** A tap opens the tooltip without moving focus, so tapping elsewhere has to close it. */
    handleDocumentClick(event) {
      if (!this.isOpen || this.contains(event.target)) return;

      this.close();
    }
  }

  if (!customElements.get("availability-tooltip")) {
    customElements.define("availability-tooltip", AvailabilityTooltip);
  }
})();
